import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiUser, PapiGallection } from '@wix/da-papi-types';
import Popover from '@wix/da-ds/pkg/popovers/dsPresets/Popover';
import PositionOnlyPopperPopover from '@wix/da-ds/pkg/popper/presets/PositionOnlyPopperPopover';

import GallectionListItemButton from '../../GallectionListItem/GallectionListItemButton';
import ScrollableContent from '@wix/da-ds/pkg/ScrollableContent';
import { GroupGallectionType } from '../redux/types';

import s from './GroupGallectionPopover.scss';

export interface Props {
  isReduxReady?: boolean;
  isVisible?: boolean;
  gallectionType: GroupGallectionType;
  group: PapiUser;
  selected?: PapiGallection;
  onSelect?: (gallection: PapiGallection) => void;
  gallections?: PapiGallection[];
  fetchGallections: (groupid: number, type: 'gallery' | 'collection') => void;
  requestClose: () => void;
}

const GroupGallectionPopover: React.FC<Props> = ({
  isVisible,
  isReduxReady,
  group,
  selected,
  onSelect,
  gallections,
  gallectionType,
  fetchGallections,
  requestClose,
}) => {
  const { t } = useTranslation();
  const [isFetching, setIsFetching] = useState(false);
  const { userId } = group;

  // Fetch gallections
  useEffect(() => {
    if (!gallections && !isFetching && isReduxReady) {
      setIsFetching(true);
      fetchGallections(userId, gallectionType);
    }
  }, [
    gallections,
    isReduxReady,
    isFetching,
    fetchGallections,
    gallectionType,
    userId,
  ]);

  // Preselect featured folder
  useEffect(() => {
    if (selected || !onSelect) {
      return;
    }
    if (gallections?.length) {
      onSelect(gallections[0]);
    }
  }, [gallections, selected, onSelect]);

  if (!isVisible) {
    return null;
  }

  return (
    <PositionOnlyPopperPopover
      width={390}
      onClickOutside={requestClose}
      placement="bottom"
      secondaryPlacement="end"
      usePortal={true}
      popoverContent={
        <Popover
          heading={t('groupGallectionSelector.popover.heading', {
            groupname: group.username,
          })}
          onCloseClick={requestClose}
          className={s['root']}
          contentClassName={s['content']}
          showHeaderBorder
          content={
            <ScrollableContent className={s['scrollable']}>
              {gallections?.map(gallection => (
                <GallectionListItemButton
                  key={gallection.folderId}
                  gallection={gallection}
                  onClick={() => {
                    onSelect && onSelect(gallection);
                    requestClose();
                  }}
                  className={s['item']}
                  checked={gallection.folderId === selected?.folderId}
                />
              ))}
            </ScrollableContent>
          }
        />
      }
    />
  );
};
GroupGallectionPopover.displayName = 'GroupGallectionPopover';

export default GroupGallectionPopover;
